import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of, ReplaySubject } from 'rxjs';
import { map, tap, catchError, shareReplay } from 'rxjs/operators';
import { OrdersService } from '@infra-services/orders/orders.service';
import { PatientService } from '@infra-services/patients/patient.service';
import { CatalogRepository } from '@service/catalog/catalog.repository';

interface CatalogItem {
  id: string;
  name: string;
}

interface CatalogCache {
  specimenTypes: Map<string, string>;
  specimenContainers: Map<string, string>;
  examTypes: Map<string, string>;
  diagnoses: Map<string, string>;
  specialists: Map<string, string>;
  providers: Map<string, string>;
  insuranceCompanies: Map<string, string>;
  regionals: Map<string, string>;
  sites: Map<string, string>;
  reprocessCauses: Map<string, string>;
  reprocessStatuses: Map<string, string>;
  priorities: Map<string, string>;
  laboratories: Map<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class CatalogResolverService {
  private readonly ordersService = inject(OrdersService);
  private readonly patientService = inject(PatientService);
  private readonly catalogRepository = inject(CatalogRepository);

  private catalogCache: CatalogCache | null = null;
  private readonly loadingSubject = new ReplaySubject<CatalogCache>(1);
  private isLoading = false;

  loadCatalogs(): Observable<CatalogCache> {
    if (this.catalogCache) {
      return of(this.catalogCache);
    }

    if (this.isLoading) {
      return this.loadingSubject.asObservable();
    }

    this.isLoading = true;

    return forkJoin({
      specimenTypes: this.patientService
        .getSpecimenTypes()
        .pipe(catchError(() => of([]))),
      specimenContainers: this.ordersService
        .getSpecimenContainers()
        .pipe(catchError(() => of([]))),
      examTypes: this.ordersService.getExamTypes().pipe(
        map((items) => items.map((i) => ({ id: i.code, name: i.name }))),
        catchError(() => of([]))
      ),
      diagnoses: this.ordersService
        .getDiagnoses()
        .pipe(catchError(() => of([]))),
      specialists: this.ordersService
        .getSpecialists()
        .pipe(catchError(() => of([]))),
      providers: this.ordersService
        .getProviders()
        .pipe(catchError(() => of([]))),
      insuranceCompanies: this.ordersService
        .getInsuranceCompanies()
        .pipe(catchError(() => of([]))),
      regionals: this.ordersService.getRegions().pipe(
        map((items) => items.map((i) => ({ id: i.id, name: i.name }))),
        catchError(() => of([]))
      ),
      sites: this.ordersService.getSites().pipe(
        map((items) => items.map((i) => ({ id: i.id, name: i.name }))),
        catchError(() => of([]))
      ),
      reprocessCauses: this.ordersService
        .getReprocessCauses()
        .pipe(catchError(() => of([]))),
      reprocessStatuses: this.ordersService
        .getReprocessStatuses()
        .pipe(catchError(() => of([]))),
      labReprocessCauses: this.catalogRepository.getAllCauses().pipe(
        map((items) => items.map((i) => ({ id: i.id, name: i.description }))),
        catchError(() => of([]))
      ),
      labReprocessStatuses: this.catalogRepository.getReprocessStatuses().pipe(
        map((items) => items.map((i) => ({ id: i.id, name: i.description }))),
        catchError(() => of([]))
      ),
      priorities: this.ordersService
        .getPriorities()
        .pipe(catchError(() => of([]))),
      laboratories: this.ordersService
        .getLaboratories()
        .pipe(catchError(() => of([])))
    }).pipe(
      map((catalogs) => this.buildCache(catalogs)),
      tap((cache) => {
        this.catalogCache = cache;
        this.loadingSubject.next(cache);
        this.isLoading = false;
      }),
      shareReplay(1)
    );
  }

  private buildCache(catalogs: Record<string, CatalogItem[]>): CatalogCache {
    const combinedReprocessCauses = this.mergeMaps(
      this.arrayToMap(catalogs['reprocessCauses']),
      this.arrayToMap(catalogs['labReprocessCauses'])
    );

    const combinedReprocessStatuses = this.mergeMaps(
      this.arrayToMap(catalogs['reprocessStatuses']),
      this.arrayToMap(catalogs['labReprocessStatuses'])
    );

    return {
      specimenTypes: this.arrayToMap(catalogs['specimenTypes']),
      specimenContainers: this.arrayToMap(catalogs['specimenContainers']),
      examTypes: this.arrayToMap(catalogs['examTypes']),
      diagnoses: this.arrayToMap(catalogs['diagnoses']),
      specialists: this.arrayToMap(catalogs['specialists']),
      providers: this.arrayToMap(catalogs['providers']),
      insuranceCompanies: this.arrayToMap(catalogs['insuranceCompanies']),
      regionals: this.arrayToMap(catalogs['regionals']),
      sites: this.arrayToMap(catalogs['sites']),
      reprocessCauses: combinedReprocessCauses,
      reprocessStatuses: combinedReprocessStatuses,
      priorities: this.arrayToMap(catalogs['priorities']),
      laboratories: this.arrayToMap(catalogs['laboratories'])
    };
  }

  private mergeMaps(
    map1: Map<string, string>,
    map2: Map<string, string>
  ): Map<string, string> {
    const merged = new Map(map1);
    for (const [key, value] of map2) {
      merged.set(key, value);
    }
    return merged;
  }

  private arrayToMap(items: CatalogItem[]): Map<string, string> {
    const map = new Map<string, string>();
    if (items && Array.isArray(items)) {
      for (const item of items) {
        if (item?.id != null) {
          const idStr = String(item.id);
          map.set(idStr, item.name ?? idStr);
        }
      }
    }
    return map;
  }

  resolveValue(fieldKey: string, value: string): string {
    if (!this.catalogCache || !value) {
      return value;
    }

    const normalizedKey = fieldKey.toLowerCase();

    const fieldToCatalog: Record<string, keyof CatalogCache> = {
      containerid: 'specimenContainers',
      specimencontainerid: 'specimenContainers',
      container: 'specimenContainers',
      specimentypeid: 'specimenTypes',
      sampletypeid: 'specimenTypes',
      tipomuestra: 'specimenTypes',
      examid: 'examTypes',
      examen: 'examTypes',
      diagnosisid: 'diagnoses',
      diagnostico: 'diagnoses',
      specialistid: 'specialists',
      especialista: 'specialists',
      providerid: 'providers',
      proveedor: 'providers',
      insurancecompanyid: 'insuranceCompanies',
      companyid: 'insuranceCompanies',
      compania: 'insuranceCompanies',
      regionalid: 'regionals',
      regional: 'regionals',
      siteid: 'sites',
      sede: 'sites',
      reprocesscauseid: 'reprocessCauses',
      causeid: 'reprocessCauses',
      causareproceso: 'reprocessCauses',
      reprocessstatusid: 'reprocessStatuses',
      statusid: 'reprocessStatuses',
      estadoreproceso: 'reprocessStatuses',
      priorityid: 'priorities',
      samplepriority: 'priorities',
      priority: 'priorities',
      specimenpriorityid: 'priorities',
      prioridad: 'priorities',
      laboratoryid: 'laboratories',
      laboratorio: 'laboratories',
      laboratory: 'laboratories'
    };

    const catalogKey = fieldToCatalog[normalizedKey];
    if (!catalogKey) {
      return value;
    }

    const catalog = this.catalogCache[catalogKey];
    const valueStr = String(value);
    return catalog?.get(valueStr) ?? value;
  }

  shouldResolve(fieldKey: string): boolean {
    const normalizedKey = fieldKey.toLowerCase();
    const resolvableFields = [
      'containerid',
      'specimencontainerid',
      'container',
      'specimentypeid',
      'sampletypeid',
      'tipomuestra',
      'examid',
      'examen',
      'diagnosisid',
      'diagnostico',
      'specialistid',
      'especialista',
      'providerid',
      'proveedor',
      'insurancecompanyid',
      'companyid',
      'compania',
      'regionalid',
      'regional',
      'siteid',
      'sede',
      'reprocesscauseid',
      'causeid',
      'causareproceso',
      'reprocessstatusid',
      'statusid',
      'estadoreproceso',
      'priorityid',
      'samplepriority',
      'priority',
      'prioridad',
      'specimenpriorityid',
      'laboratoryid',
      'laboratorio',
      'laboratory'
    ];
    return resolvableFields.includes(normalizedKey);
  }

  resolveObject(obj: Record<string, unknown>): Record<string, unknown> {
    if (!this.catalogCache || !obj) {
      return obj;
    }

    const resolved: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string' && this.shouldResolve(key)) {
        resolved[key] = this.resolveValue(key, value);
      } else {
        resolved[key] = value;
      }
    }
    return resolved;
  }
}
